import React, { useEffect } from 'react';
import IdleChecker from 'components/Global/IdleChecker';
import CartDrawer from 'components/Cart/CartDrawer';
import Header from 'components/Global/Header';
import Footer from 'components/Global/Footer';
import CookieBanner from 'components/Global/CookieBanner';
import InAppBrowserSignInGate from 'components/Global/InAppBrowserSignInGate';
import { useRouter } from 'next/router';
import {
  useContent, useMjrApiUser, useSession,
} from 'utils/swrCalls';
import applicationInsights from 'utils/applicationInsights';
import { isMobileUserAgent } from 'utils/inAppBrowser';

/**
 * GlobalLayout
 *
 * @param props
 *
 * Layout component that wraps all child components in app
 */
const GlobalLayout = (props) => {
  const { children } = props;

  const { user: session, isLoading } = useSession();
  const router = useRouter();
  const userNeedsToLogIn = !session && !isLoading && router.pathname !== '/review-estimate';
  // Mobile-only, flag-gated: an in-app browser (opened from a link inside an email/social app) can
  // lose its own tab/session when the user switches away to read a password-reset code, leaving
  // them unable to finish. Desktop is untouched either way - this only ever renders on mobile, and
  // is fully inert (same instant redirect as before) with the flag off.
  const showInAppBrowserGate = userNeedsToLogIn
    && process.env.NEXT_PUBLIC_LOGIN_IN_APP_BROWSER_GATE === 'true'
    && typeof window !== 'undefined'
    && isMobileUserAgent(window.navigator.userAgent);
  if (userNeedsToLogIn && !showInAppBrowserGate) window.location.href = '/api/auth/social-login';
  const { data: user, error: userError, isValidating: userIsValidating } = useMjrApiUser();
  const { data: content } = useContent();

  // SWR's key for useMjrApiUser() is conditional on `session`, so the render where `session`
  // first becomes truthy is a transitional one: the new fetch hasn't started yet, leaving both
  // `data` and `isValidating` at their stale pre-login values (undefined / false). Requiring the
  // request to have actually settled (data or error defined) avoids treating that transitional
  // render as "checked and found no customer".
  const userLookupSettled = (user !== undefined || userError !== undefined) && !userIsValidating;
  const userNeedsToBeRegisteredInBackEnd = (session && !user && userLookupSettled && router.pathname !== '/review-estimate');
  if (userNeedsToBeRegisteredInBackEnd && router.pathname !== '/create-account') router.push('/create-account');

  // Safety net for the class of bug where the authenticated session ends up associated with the
  // wrong customer record (e.g. a shared/stale token cache) - Customer.Email is looked up server
  // side using the session's own email, so in the normal case these can never disagree. If they
  // ever do, someone is looking at a customer record that isn't theirs; alert on it immediately
  // rather than waiting for a customer to notice and report it.
  useEffect(() => {
    if (!session?.email || !user?.email) return;
    if (session.email.toLowerCase() !== user.email.toLowerCase()) {
      applicationInsights.trackEvent({ name: 'IdentityCustomerMismatch' }, {
        sessionEmail: session.email,
        sessionSub: session.sub,
        customerEmail: user.email,
        customerId: user.customerId,
      });
    }
  }, [session?.email, session?.sub, user?.email, user?.customerId]);

  if (showInAppBrowserGate) {
    return <InAppBrowserSignInGate />;
  }

  if (router.pathname === '/review-estimate') {
    return (
      <div>
        <Header />
        <div className='main container'>
          {children}
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <>
      {session
      && (user?.customerId || router.pathname === '/create-account')
      && content
      && (
      <div>
        <IdleChecker />
        <Header />
        <div className='main container'>
          {children}
          <CartDrawer />
        </div>
        <Footer />
        <CookieBanner />
      </div>
      )}
    </>
  );
};

export default GlobalLayout;
